import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import {
	BindingEngine,
	CompositionTransaction,
	computedFrom,
	Disposable,
	inject,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { NavigationInstruction, RouteConfig, Router } from 'aurelia-router';
import {
	bindable,
	CompositionTransactionNotifier,
	InlineViewStrategy,
} from 'aurelia-templating';
import { BindingSignaler } from 'aurelia-templating-resources';
import httpStatus from 'http-status';

import { ClassicFormsApi } from '~/api/classic-forms-api';
import { CustomisationsApi } from '~/api/customisations-api';
import { APIParameters, GenericApi } from '~/api/generic-api';
import { AppRouteID } from '~/app-routes';
// tslint:disable-next-line no-circular-imports
import {
	AcurityTable,
	AcurityTableConfig,
	DataTableRequestFilter,
	PageIdentifier,
	RecordIdentifier,
	TableColumnDefinition,
} from '~/components/acurity-table/acurity-table';
import { bindAcurityTableShortcuts } from '~/components/acurity-table/acurity-table-shortcuts';
import { ClassicPanel } from '~/components/classic-panel/classic-panel';
import { ClassicPanelDialogModel } from '~/components/classic-panel/classic-panel-dialog';
import { ClassicPanelDialogUtils } from '~/components/classic-panel/classic-panel-dialog-utils';
import { ContextMenuItem } from '~/components/context-menu-list/context-menu-list';
import {
	CustomisationConfig,
	FunctionConfigBackend,
} from '~/components/customisation/customisation-config';
import { BaseDialog } from '~/components/dialog/base-dialog';
import {
	SimpleDialog,
	SimpleDialogButtons,
	SimpleDialogType,
} from '~/components/dialog/simple-dialog';
import { SearchFieldModelMap } from '~/components/field/common/search-definition';
import { Filter } from '~/components/filter/filter';
import {
	FilterType,
	getFilterOption,
} from '~/components/filter/filter-options';
import { FormType } from '~/components/form/form';
import { NavigationState } from '~/components/navigation/navigation-state';
import { Notification } from '~/components/notification/notification';
// tslint:disable-next-line no-circular-imports
import { Slideout } from '~/components/slideout/slideout';
import { CommonURLParam, ShortcutKey } from '~/constants';
import {
	ExcelExportDialog,
	ExcelExportDialogModel,
} from '~/forms/excel-export-dialog';
import I18nForm from '~/forms/i18n-form';
import { DataType, DataTypeEnum } from '~/models/data-type';
import { FormModel } from '~/models/form-model';
import { FormParameters, FormParameterType } from '~/models/form-parameters';
import { HasCustomisationConfig } from '~/models/page-controller';
import {
	AcurityFieldUtils,
	FieldNameParamType,
} from '~/utils/acurity-field-utils';
import { DeleteHelper } from '~/utils/delete-helper';
import { DialogUtils } from '~/utils/dialog-utils';
import { ExportColumn } from '~/utils/export';
import { FilterUtils } from '~/utils/filter-utils';
import FormModelUtils from '~/utils/form-model-utils';
import { JQueryHelper } from '~/utils/jquery-helper';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';
import { pageErrorHandler } from '~/utils/page-error-handler';
import { ObjectUtils } from '~/utils/utils';
import { NavigationMenuItem } from '~/components/navigation/navigation-types';
import { ClassicFormSettings } from '~/forms/classic-form-settings';
import { ClassicFormSettingsHolder } from '~/forms/classic-form-settings-holder';

// Can't use autoinject here as that causes injection of all constructor arguments in ...rest array
// including those we intend to pass from derived classes. Use inject to explicitly inject aurelia dependencies.
@inject(
	ClassicFormsApi,
	CompositionTransaction,
	CustomisationsApi,
	BindingEngine,
	Router,
	I18N,
	BindingSignaler,
	EventAggregator,
	Element,
	DialogUtils,
	ClassicPanelDialogUtils,
	NavigationState,
)
// NOTE: Jake: 2019-06-20 - WEBUI-2040
// We should not set @useView() here as classes that have no explicit useView() will end up breaking
// or not using their template as we'd expect.
// @useView(PLATFORM.moduleName('~/forms/classic-form.html'))
export default class ClassicForm<FieldNameType extends string = string>
	extends I18nForm
	implements HasCustomisationConfig
{
	public customisationConfig: CustomisationConfig | false;
	protected nonCustomisableAreaHtml: string;

	public recordSelectorTable: AcurityTable;
	public recordId: number;

	protected readonly viewMenuId: string = 'viewMenuId';
	protected readonly editMenuId: string = 'editMenuId';
	protected readonly deleteMenuId: string = 'deleteMenuId';
	protected readonly auditDetailsMenuId: string = 'auditDetailsMenuId';
	protected readonly notesMenuId: string = 'notesMenuId';
	protected readonly exportMenuId: string = 'exportMenuId';
	protected readonly cloneMenuId: string = 'cloneMenuId';

	public canClick: boolean = true;
	public recordTableFilter: Filter;
	public slideOut: Slideout;
	public eventSubscriptions: Subscription[] = [];
	public thisForm: ClassicForm<FieldNameType> = this;
	// used to ensure certain records that meet a condition can not be edited/deleted
	// tslint:disable-next-line:variable-name
	private _selectedRecordIsReadOnly: boolean = false;

	// allows functionName to depend on page query parameters in subclasses
	public readonly functionNameBase: string;

	/**
	 * hasFilteringEnabled will allow a user to filter on records using a filter slideout/panel
	 * when this value is true.
	 */
	protected hasFilteringEnabled: boolean = true;

	protected auditDialogColumnNames: string[];

	/**
	 * Table confirm for Audit dialog
	 */
	protected auditDialogTableConfig?: AcurityTableConfig = {
		tableColumns: [],
		multiColumnOrder: [],
		rowIdKey: '',
		defaultSortField: '',
		deletedHighlightField: '',
		defaultSortDirection: 'desc',
		isAjaxTable: false,
		isLoading: false,
	};

	protected isRowLoading: boolean = false;

	/**
	 * This is an optional attribute used as a hint while rendering the context menu.
	 * Components which extend classic-form may set a value.
	 */
	protected contextMenuMaxItemsCount: number;

	@bindable
	protected tableConfig: AcurityTableConfig<FieldNameType> = {
		tableColumns: [],
		multiColumnOrder: [],
		rowIdKey: '',
		defaultSortField: '',
		deletedHighlightField: '',
		defaultSortDirection: 'desc',
		isAjaxTable: true,
		isLoading: false,
	};

	/**
	 * readOnlyForm is used to make the form not-editable.
	 */
	public readOnlyForm: boolean = false;

	protected extraReadOnlyParameters: string[] = [];

	public readOnlyBySelected: Array<{
		selectedParam: FieldNameType;
		field: FieldNameType;
	}> = [];

	protected selectedParameters: FormParameters[] = [];

	// Holds the table filter objects constructed from query parameters.
	// Subclasses can use it to populate specific filters other than general filters for fund, member etc
	private paramFilters: DataTableRequestFilter[] = [];

	// Fields which are not part of formmodel but are required as part of request
	public queryParams: Map<string, string> = new Map();

	// Query parameters that ar required as part of a delete request because the form has different modes.
	// These parameters may affect how the delete is done or how the returned formmodel is built.
	public deleteQueryParams: Map<string, string> = new Map();

	protected deferAjaxTableDataLoad: boolean = false;

	protected addCustomisableAreaName: string = 'addRecord';

	protected editCustomisableAreaName: string = 'singleRecordDisplay';

	/**
	 * isCustomisationOptional will stop an error being thrown if the customisation API gets a 404.
	 * This needs to be enabled for forms where customisation JSON or HTML files dont exist, like reporting-centre
	 */
	protected isCustomisationOptional: boolean = false;

	private subscription: Disposable;

	protected readonly fieldNamesToFormParamType: readonly FieldNameParamType[] =
		this.initFormParameterTypes();

	// true if formParameters must be present in url. By default {@link ClassicForm#formParameters} are treated as optional.
	protected requireParams: boolean = false;

	/**
	 * This array provides a mapping from url param to its missing error transaltion. It is array to preserve
	 * an order of parameter weight. Primary params weigh more when they are missing than secondary params.
	 */
	public missingURLParamErrorMap: ReadonlyArray<readonly string[]> = [
		// Primary parameters (correlates to EntityType)
		[CommonURLParam.MEMBER, this.i18n.tr('errors.must-select-a-member')],
		[
			CommonURLParam.ADVISER,
			this.i18n.tr('errors.an-adviser-must-be-selected'),
		],
		[
			CommonURLParam.EMPLOYER,
			this.i18n.tr('errors.an-employer-must-be-selected'),
		],
		[
			CommonURLParam.INVESTMENT_MANAGER,
			this.i18n.tr('errors.a-manager-must-be-selected'),
		],
		// Secondary parameters (shared across some EntityTypes, not very user-friendly)
		[CommonURLParam.CLIENT, this.i18n.tr('errors.must-select-a-client')],
		[
			CommonURLParam.CATEGORY,
			this.i18n.tr('errors.a-category-must-be-selected'),
		],
		[CommonURLParam.FUND, this.i18n.tr('errors.a-fund-must-be-selected')],
	];

	/**
	 * Context menu event listeners
	 */
	protected contextMenuListeners: { [name: string]: EventListener } = {};

	private headerButtonsInlineViewStrategy?: InlineViewStrategy;

	protected searchFieldModelMap: SearchFieldModelMap;

	protected currentApiVersion?: string | undefined;

	public formControlsService: ClassicFormSettings;

	protected archiveInProgress: boolean = false;
	protected archiveDbUpgradeInProgress: boolean = false;

	constructor(
		public functionName: string,
		public canSave: boolean,
		public canDelete: boolean,
		private _hasNotes: boolean,
		private _hasAuditDetails: boolean,
		public canAdd: boolean,
		readonly visibleColumns: number,
		readonly formParameters: FieldNameType[],
		protected classicFormsApi?: ClassicFormsApi,
		protected compositionTransaction?: CompositionTransaction,
		protected customisationsApi?: CustomisationsApi,
		protected bindingEngine?: BindingEngine,
		router?: Router,
		i18n?: I18N,
		bindingSignaler?: BindingSignaler,
		eventAggregator?: EventAggregator,
		element?: Element,
		readonly dialogUtils?: DialogUtils,
		readonly classicPanelDialogUtils?: ClassicPanelDialogUtils,
		protected readonly navState?: NavigationState,
		langNamespaces?: string[],
	) {
		super(
			router,
			i18n,
			bindingSignaler,
			eventAggregator,
			element,
			langNamespaces,
		);
		if (
			!(
				this.classicFormsApi &&
				this.compositionTransaction &&
				this.customisationsApi &&
				this.router &&
				this.i18n &&
				this.bindingSignaler &&
				this.eventAggregator &&
				this.element &&
				this.dialogUtils &&
				this.classicPanelDialogUtils &&
				this.navState
			)
		) {
			// Unfortunately we have to make all the aurelia injected arguments optional or else
			// we can't pass ...rest into it.
			throw new Error('Missing ...rest in subclass');
		}
		this.functionNameBase = functionName;
		this.classicFormsApi.setCurrentApiVersion(undefined);
		this.headerButtonsInlineViewStrategy =
			this.makeHeaderButtonsInlineViewStrategy();
		// need to bind the context of classic-form to this method, so that when
		// the method is called in a event handler, `this` will be pointed to
		// `classic-form` instead of the global object which is `window`
		this.changeSlideOutSize = this.changeSlideOutSize.bind(this);

		this.formControlsService = new ClassicFormSettings(
			new ClassicFormSettingsHolder(
				this.canAdd,
				this.canSave,
				this.canDelete,
				this.hasAuditDetails,
				this.hasNotes,
			),
			this.navState,
		);
	}

	/**
	 * Create classic form header buttons inline view for {@link ClassicForm.headerButtonsInlineViewStrategy}.
	 */
	protected makeHeaderButtonsInlineViewStrategy() {
		return new InlineViewStrategy(
			`<template>
				<button
					if.bind="showAddRecord"
					class="i-btn btn btn-primary"
					type="button"
					click.delegate="onClickAddRecord()"
					disabled.bind="!canAddRecord || readOnlyForm"
				>
					<span class="fas fa-plus"></span>
					<span t="classic-panel-dialog.add-record"></span>
				</button>
			<template>`,
		);
	}

	/**
	 * For displaying customised slideout title;
	 * If return undefined, slideOut will use the default "Selected Record".
	 */
	protected get slideOutTitle(): string | undefined {
		return undefined;
	}

	@computedFrom('slideOut.formModel')
	get formModel(): FormModel<FieldNameType> | undefined {
		if (this.slideOut) {
			return this.slideOut.formModel as FormModel<FieldNameType>;
		}
		return undefined;
	}

	@computedFrom('formModel')
	public get isRecordDeleted(): boolean {
		// NOTE: Jake: 2019-08-07 - WEBUI-2182
		// Not sure why we get the deleted field state with "FormModelUtils.getDeletedField"
		// and not with "deletedHighlightField".
		// const fieldModel = this.formModel ? this.formModel.fields[this.tableConfig.deletedHighlightField] : undefined;
		const fieldModel = FormModelUtils.getDeletedField(this.formModel);
		if (fieldModel) {
			return fieldModel.value === 'Y';
		}
		return false;
	}

	public get isUnDeletedAvailable(): boolean {
		return true;
	}

	@computedFrom(
		'formModel',
		'canSave',
		'canDelete',
		'hasNotes',
		'hasAuditDetails',
		'slideOut.canOpenAuditDetails',
		'canAdd',
		'readOnlyForm',
		'recordSelectorTable.excludeClone',
	)
	protected get contextMenuItemsClassicForm(): ContextMenuItem[] {
		if (this.isRowLoading) {
			// Do not return any menu items if load is in progress.
			// This prevents incorrect menu items from rendering for the current row.
			return [];
		}

		const result: ContextMenuItem[] = [
			{
				id: this.viewMenuId,
				label: this.i18n.tr('clickable-elements.view'),
				iconClass: 'fa-folder-open',
				onClick: () => this.contextView(),
				tooltip:
					'{{label}} (' +
					KeyboardShortcut.keysToString(ShortcutKey.View) +
					')',
			},
			{
				id: this.exportMenuId,
				label: this.i18n.tr('clickable-elements.export-to-excel'),
				iconClass: 'fa-file-excel',
				onClick: () => this.contextExport(),
				tooltip:
					'{{label}} (' +
					KeyboardShortcut.keysToString(ShortcutKey.Export) +
					')',
			},
		];
		if (this.canSave) {
			result.push({
				id: this.editMenuId,
				label: this.i18n.tr('clickable-elements.edit'),
				iconClass: 'fa-pencil-alt',
				onClick: () => this.contextEdit(),
				tooltip:
					'{{label}} (' +
					KeyboardShortcut.keysToString(ShortcutKey.Edit) +
					')',
				disabled: this.selectedRecordIsReadOnly || this.readOnlyForm,
			});
		}
		if (this.canDelete) {
			result.push({
				id: this.deleteMenuId,
				label:
					this.isRecordDeleted && this.isUnDeletedAvailable
						? this.i18n.tr('clickable-elements.un-delete')
						: this.i18n.tr('clickable-elements.delete'),
				iconClass: 'fa-times',
				onClick: () => this.contextDelete(),
				disabled:
					this.selectedRecordIsReadOnly ||
					this.readOnlyForm ||
					(this.isRecordDeleted && !this.isUnDeletedAvailable),
				tooltip:
					'{{label}} (' +
					KeyboardShortcut.keysToString(ShortcutKey.Delete) +
					')',
			});
		}
		if (this._hasNotes) {
			result.push({
				id: this.notesMenuId,
				label: this.i18n.tr('clickable-elements.notes'),
				iconClass: 'fa-sticky-note',
				onClick: () => this.contextNotes(),
				tooltip:
					'{{label}} (' +
					KeyboardShortcut.keysToString(ShortcutKey.Notes) +
					')',
			});
		}
		if (this._hasAuditDetails) {
			result.push({
				id: this.auditDetailsMenuId,
				label: this.i18n.tr('clickable-elements.audit-details'),
				iconClass: 'fa-history',
				disabled: !this.slideOut.canOpenAuditDetails,
				onClick: () => this.contextAuditDetails(),
				tooltip:
					'{{label}} (' +
					KeyboardShortcut.keysToString(ShortcutKey.Audit) +
					')',
			});
		}
		// NOTE: Jake: 2019-09-12 - WEBUI-2348
		// Disabled as cloning behaviour is put on-hold, as per this JIRA.
		// We may want to uncomment this and bring it back in the future
		/* if (this.canAdd &&
			!this.recordSelectorTable.excludeClone) {
			result.push({
				id: this.cloneMenuId,
				label: this.i18n.tr('clickable-elements.clone'),
				iconClass: 'fa-clone',
				onClick: () => this.contextCloneCurrentlySelectedRecord(),
			});
		}*/
		return result;
	}

	@computedFrom('contextMenuItemsClassicForm')
	protected get contextMenuItems(): ContextMenuItem[] {
		return this.contextMenuItemsClassicForm;
	}

	/**
	 * Handle notifications from child elements
	 */
	public initEventHandlers() {
		this.subscribeToEvent(Slideout.Events.CloseRecord, () =>
			this.returnFocusToSelectedRow(),
		);
		this.subscribeToEvent(Slideout.Events.PreSave, () =>
			this.disableClickableElements(),
		);
		this.subscribeToEvent(Slideout.Events.PostSave, () => {
			this.enableClickableElements();
			if (this.recordSelectorTable.isAjaxTable) {
				this.recordSelectorTable.refreshAjaxTable(false);
			}
		});
		this.subscribeToEvent(Filter.Events.OpenFilterSlideout, (data) => {
			if (
				this.recordTableFilter &&
				data &&
				data.source === this.recordTableFilter.nodeId &&
				!this.formIsEditingRecord()
			) {
				this.closeRecord();
			}
		});
		this.subscribeToEvent(
			AcurityTable.Events.Attached,
			(data: { table: AcurityTable }) => {
				if (data.table === this.recordSelectorTable) {
					if (this.recordSelectorTable) {
						this.initRecordSelectorTable();
					}
				}
			},
		);
		this.subscribeToEvent(AcurityTable.Events.ShowContextMenu, (data) => {
			if (
				this.recordSelectorTable &&
				data.source === this.recordSelectorTable.tableId
			) {
				this.initContextRow();
				// this.initContextMenuEvent();
				this.updateSelectedRow();
			}
		});

		this.subscribeToEvent(AcurityTable.Events.InitContextMenuEvents, () => {
			this.initSlideOutSize();
		});

		this.subscribeToEvent(BaseDialog.Events.CloseBaseDialog, () =>
			this.returnFocusToSelectedRow(),
		);

		this.subscribeToEvent(ClassicPanel.Events.PostPopulate, (panelData) => {
			// Try and get the ID of the row for which data was populated
			let populatedRowId = null;
			if (this.tableConfig.rowIdKey) {
				populatedRowId = this.getFormModelFieldValue(
					this.tableConfig.rowIdKey,
					panelData.data.formModel,
				);
			}

			/**
			 * We check if the current row was indeed the one whose data was populated.
			 * When the user quickly right-clicks on multiple rows, isRowLoading will become false when any of those
			 * rows finishes loading; not necessarily when the current row finishes.
			 * This may result in the context menu showing incorrect items till the current row is eventually loaded.
			 *
			 * However, rowIdKey is an optional attribute. We cannot determine if the incoming row data matches the
			 * current recordId without it. We bypass the check if rowIdKey is not available, otherwise isRowLoading
			 * will never become false.
			 */
			if (
				populatedRowId == null ||
				parseInt(populatedRowId, 10) === this.recordId
			) {
				this.isRowLoading = false;
			}
		});
	}

	protected activate(
		params: { [param: string]: any },
		routeConfig: RouteConfig,
		navigationInstruction: NavigationInstruction,
	): Promise<any> {
		const parentPromise = super.activate(
			params,
			routeConfig,
			navigationInstruction,
		);

		const activationParametersPromise =
			this.setActivationParameters(params);

		// this process is put under activate because the processed resource by i18nConfigLabelService will only
		// be avaiable in this lifecycle hook.
		if (this.requireParams) {
			this.validateUrlParameters();
		}
		// NOTE(Lucas): 2019-02-06 - WEBUI-1012
		// If the activation strategy for the form is is 'invoke-lifecycle', the customisationConfig property may be
		// already set, and not need to be changed.
		let customisationPromise;
		const previousFormName = GenericApi.formName;
		const previousCustomisationName = GenericApi.customisationName;
		const formName = CustomisationsApi.getFormNameOnActivate(
			routeConfig,
			this.calculateFormNameEnding(params),
		);
		const customisationName =
			CustomisationsApi.getCustomisationNameOnActivate(params);

		GenericApi.formName = formName;
		GenericApi.customisationName = customisationName;

		if (
			this.customisationsApi &&
			(!this.customisationConfig ||
				previousCustomisationName !== customisationName ||
				previousFormName !== formName)
		) {
			customisationPromise = this.customisationsApi
				.getCustomisation(formName, customisationName)
				.then((customisationConfig) => {
					this.customisationConfig = customisationConfig;
				})
				.catch((e) => {
					if (
						this.isCustomisationOptional &&
						e &&
						e.status === httpStatus.NOT_FOUND
					) {
						// Ignore error if not found
						return;
					}
					throw e;
				});
		}

		const pathFormName =
			this.navState.convertToPathSubtypeFormName(formName);
		const menuItems: NavigationMenuItem[] =
			this.navState.getCurrentMenuItems(
				params[CommonURLParam.NAV_VIEW],
				pathFormName,
				customisationName,
			);
		this.readOnlyForm = this.navState.computeReadOnlyForm(menuItems);

		this.formControlsService.applyBy(menuItems);

		// for a classic-form, `this.canDelete` doesn't hold the original value
		// `this.permissionService.canDelete()` must be used to check
		// if the delete control is enabled
		this.canDelete = this.formControlsService.canDelete();

		if (params.useArchive) {
			this.queryParams.set(CommonURLParam.USE_ARCHIVE, 'true');
			this.canAdd =
				this.canSave =
				this.canDelete =
				this._hasAuditDetails =
				this._hasNotes =
					false;
		}

		const promiseArray: Array<Promise<any>> = [
			parentPromise,
			customisationPromise,
			activationParametersPromise,
		];
		if (
			formName === AppRouteID.ClientDeidentification ||
			params.useArchive
		) {
			const inProgressArchive: Promise<FormModel> =
				this.classicFormsApi.getLatestRetentionProcessingInProgress(
					'A',
				);
			inProgressArchive.then((formModel: FormModel) => {
				if (formModel != null) {
					if (formModel.fields.RPz_Table_Code.value === '') {
						this.archiveDbUpgradeInProgress = true;
					} else {
						this.archiveInProgress = true;
					}
				}
			});
			promiseArray.push(inProgressArchive);
		}

		return Promise.all(promiseArray).catch(pageErrorHandler);
	}

	@computedFrom('_hasNotes')
	get hasNotes() {
		return this._hasNotes;
	}

	@computedFrom('_hasAuditDetails')
	get hasAuditDetails() {
		return this._hasAuditDetails;
	}

	protected calculateFormNameEnding(params) {
		return '';
	}

	/**
	 * Default form parameter associations.
	 */
	protected initFormParameterTypes(): readonly FieldNameParamType[] {
		return AcurityFieldUtils.defaultFieldTypes;
	}

	/**
	 * showAddRecord will return false if the "Add Record" button is disabled for this form.
	 * ie. readonly form
	 */
	@computedFrom('canAdd', 'hasErrorMessage')
	get showAddRecord(): boolean {
		return this.canAdd && !this.hasErrorMessage;
	}

	/**
	 * canAddRecord will show the "Add Record" button in a disabled state.
	 * ie. if you're editing a record already
	 */
	@computedFrom(
		'showAddRecord',
		'slideOut',
		'slideOut.isInEditMode',
		'readOnlyForm',
	)
	get canAddRecord(): boolean {
		return (
			this.showAddRecord &&
			(!this.slideOut || !this.slideOut.isInEditMode) &&
			!this.readOnlyForm
		);
	}

	private onClickAddRecord(): void {
		if (!this.readOnlyForm) {
			this.openAddNewRecord();
		}
	}

	public setActivationParameters(
		params: { [paramName: string]: any },
		customisationConfig?: CustomisationConfig,
	): Promise<void> {
		if (customisationConfig) {
			this.customisationConfig = customisationConfig;
		}
		if (this.slideOut && this.slideOut.isOpen()) {
			this.slideOut.close();
		}
		if (this.recordTableFilter) {
			this.recordTableFilter.close();
		}

		if (params != null && params[CommonURLParam.RECORD_ID]) {
			const recordId = Number(params[CommonURLParam.RECORD_ID]);
			this.openRecord(recordId);
		}

		const compositionTransactionNotifier: CompositionTransactionNotifier =
			this.compositionTransaction.enlist();
		if (params != null) {
			this.setSelectedParameters(params);
		}
		return JQueryHelper.whenReady($('classic-panel')).then(() => {
			if (params != null) {
				// Map values from params
				// ie. "member" / "fund" to table column names
				ClassicForm.applyReadOnlyBySelected(
					this.extraReadOnlyParameters,
					this.readOnlyBySelected,
					this.selectedParameters,
				);
				// Add filters
				this.paramFilters =
					FilterUtils.computeFiltersFromParams(params);
			}
			compositionTransactionNotifier.done();
		});
	}

	/**
	 * Populates the selectedParameters class variable for url parameters which
	 * match field names defined in {@link ClassicForm#initFormParameterTypes}.
	 * @param params {{[paramName: string]: any}} Url parameters
	 */
	protected setSelectedParameters(params: {
		[paramName: string]: any;
	}): void {
		this.selectedParameters = [];
		for (const formParameter of this.getFormParameters()) {
			for (const fieldNameParamType of this.fieldNamesToFormParamType) {
				const paramValue = params[fieldNameParamType.paramType];
				if (
					paramValue !== undefined &&
					formParameter.endsWith(fieldNameParamType.fieldNameEndsWith)
				) {
					this.selectedParameters.push({
						paramType:
							fieldNameParamType.alias !== undefined
								? fieldNameParamType.alias
								: fieldNameParamType.paramType,
						value: paramValue,
						name: formParameter,
					});
				}
			}
		}
		if (this.slideOut) {
			this.slideOut.preSelectedParameters =
				this.getPreSelectedParametersForEdit();
		}
	}

	protected getFormParameters(): FieldNameType[] {
		return this.formParameters;
	}

	protected async openRecord(recordId: number, editMode: boolean = false) {
		if (!this.slideOut) {
			this.subscribeToEventOnce(
				Slideout.Events.Attached,
				(data: { slideout: Slideout }) =>
					this.openRecord(recordId, editMode),
				(data: { slideout: Slideout }) =>
					data.slideout === this.slideOut,
			);
			return;
		}

		this.recordId = recordId;
		this.setSelectedRecordIsReadOnly(
			this.recordSelectorTable.readOnlyRecords,
		);
		this.recordSelectorTable.setSelectedRecordId(recordId);
		await this.slideOut.showSlideout();
		if (editMode) {
			this.slideOut.enableEditMode();
		} else {
			this.slideOut.enableReadonlyMode();
		}

		if (
			!this.setFocusToSelectedRow() &&
			document.activeElement instanceof HTMLElement
		) {
			document.activeElement.blur();
		}
	}

	// tslint:disable-next-line:no-empty
	protected bind() {}

	protected attached() {
		// all subscription should be done under attached lifecycle method
		this.initEventHandlers();
		// Table Shortcuts
		bindAcurityTableShortcuts(this, {
			selectPreviousRecord: this.selectPreviousRecord,
			selectNextRecord: this.selectNextRecord,
			selectPreviousPage: this.selectPreviousPage,
			selectNextPage: this.selectNextPage,
			selectFirstPage: this.selectFirstPage,
			canUseSelectRecordOrPage: this.canUseSelectRecordOrPage,
		});
		// Classic Form
		KeyboardShortcut.subscribe(this, ShortcutKey.View, () =>
			this.contextView(),
		);
		KeyboardShortcut.subscribe(this, ShortcutKey.Close, () =>
			this.clearTableSelection(),
		);

		// TODO(Jake): 2018-10-15 - WEBUI-689
		// Simplfify how the Context menu works so that tying it to the new KeyboardShortcut paradigm
		// works cleanly.
		// Related: https://support.financialsynergy.com.au/browse/WEBUI-1091

		if (!this.readOnlyForm) {
			if (this.canDelete) {
				KeyboardShortcut.subscribe(this, ShortcutKey.Delete, () =>
					this.contextDelete(),
				);
			}
			KeyboardShortcut.subscribe(this, ShortcutKey.Edit, () =>
				this.contextEdit(),
			);
		}
		KeyboardShortcut.subscribe(this, ShortcutKey.Notes, () =>
			this.contextNotes(),
		);
		KeyboardShortcut.subscribe(this, ShortcutKey.Audit, () =>
			this.contextAuditDetails(),
		);
		// NOTE: Jake: 2019-09-12 - WEBUI-2348
		// Disabled as cloning behaviour is put on-hold, as per this JIRA.
		// We may want to uncomment this and bring it back in the future
		// KeyboardShortcut.subscribe(this, ShortcutKey.CloneSelected, () => this.contextCloneCurrentlySelectedRecord());
		{
			const elList = this.element.querySelectorAll(
				'[click\\.delegate="onClickAddRecord()"]',
			);
			if (elList.length > 0) {
				KeyboardShortcut.subscribe(this, ShortcutKey.AddRecord, elList);
			} else {
				KeyboardShortcut.subscribe(this, ShortcutKey.AddRecord, () =>
					this.contextAdd(),
				);
			}
		}

		KeyboardShortcut.subscribe(this, ShortcutKey.Export, () =>
			this.contextExport(),
		);

		if (this.slideOut) {
			this.slideOut.preSelectedParameters =
				this.getPreSelectedParametersForEdit();
			this.slideOut.extraReadOnlyParameters =
				this.extraReadOnlyParameters;
			this.slideOut.setFunctionName(this.functionName);
			this.slideOut.setQueryParams(this.queryParams);
		}

		this.subscription = this.bindingEngine
			.propertyObserver(this, 'formModel')
			.subscribe((newValue, oldValue) =>
				this.formModelChanged(newValue, oldValue),
			);

		const functionConfig: FunctionConfigBackend = (
			this.customisationConfig as CustomisationConfig
		)?.functionConfig;

		let dataTypeMap: Map<string, DataType>;
		// Construct a map of dataTypes from `fieldConfig`
		if (functionConfig?.fieldConfig?.length > 0) {
			dataTypeMap = new Map<string, DataType>();
			functionConfig.fieldConfig
				.filter((config) => config.dataType !== null)
				.forEach((config) =>
					dataTypeMap.set(config.name, config.dataType),
				);
		}

		// If tableColumnConfig is present, use the datTypeMap to set dataType on customised table colums
		if (functionConfig?.tableColumnConfig?.length > 0) {
			const mergeConfigColumns = () => {
				this.recordSelectorTable.tableColumns = this.mergeTableColumns(
					this.recordSelectorTable.tableColumns,
					functionConfig.tableColumnConfig,
					dataTypeMap,
				);
			};

			if (!this.recordSelectorTable) {
				this.subscribeToEventOnce(
					AcurityTable.Events.Attached,
					mergeConfigColumns,
					(data: { table: AcurityTable }) =>
						data.table === this.recordSelectorTable,
				);
			} else {
				mergeConfigColumns();
			}
			// If tableColumnConfig is absent, set dataTypeMap on the table so we can
			// set dataType on the backend default table columns in `acurity-table.ts`
		} else if (this.recordSelectorTable) {
			this.recordSelectorTable.fieldConfigDataTypeMap = dataTypeMap;
		}

		// WEBUI-4309: If `searchFieldModelMap` is declared on the form, these fields will be set as `search` inputs
		// in the filter panel. We only want `EQUALS` and `EMPTY` to be options for these search filters
		if (this.recordSelectorTable && this.searchFieldModelMap) {
			this.recordSelectorTable.customFilterOptions = {};
			Object.keys(this.searchFieldModelMap).forEach((fieldName) => {
				this.recordSelectorTable.customFilterOptions[fieldName] = [
					{ ...getFilterOption(FilterType.EQUALS) },
					{ ...getFilterOption(FilterType.EMPTY) },
				];
			});
		}

		if (
			this.isCustomisationOptional &&
			!this.customisationConfig &&
			this.nonCustomisableAreaHtml
		) {
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.CustomisationEditor,
				() => {
					this.showNoCustomisationDialog();
				},
			);
		}
	}

	/**
	 * @override
	 */
	protected detached(): void {
		super.detached();
		this.subscription.dispose();
	}

	protected unbind(): void {
		super.unbind();
		window.removeEventListener('resize', this.changeSlideOutSize);
	}

	/**
	 * This adds the fields specified by properties of each element of the {@link readOnlyBySelected} param to the
	 * read only parameters when the selectedParam property of the element is in the
	 * selected parameters.
	 * @param readOnlyParameters the read only parameters
	 * @param readOnlyBySelected specifies the fields to possibly add to the read only parameters
	 * @param selectedParameters the selected parameters
	 */
	private static applyReadOnlyBySelected<FieldNameType extends string>(
		readOnlyParameters: string[],
		readOnlyBySelected: Array<{
			selectedParam: FieldNameType;
			field: FieldNameType;
		}>,
		selectedParameters: FormParameters[],
	) {
		readOnlyBySelected.forEach((readOnlyBySelectedElement) => {
			const paramToAdd = selectedParameters.find(
				(p) => p.name === readOnlyBySelectedElement.selectedParam,
			);
			if (paramToAdd) {
				readOnlyParameters.push(
					readOnlyBySelectedElement.selectedParam,
				);
				readOnlyParameters.push(readOnlyBySelectedElement.field);
			}
		});
	}

	private getPreSelectedParametersForEdit(): FormParameters[] {
		// only want to preselect certain parameters, category should not be pre-populated in slideout
		return this.getPreSelectedParams([
			CommonURLParam.FUND,
			CommonURLParam.MEMBER,
			CommonURLParam.EMPLOYER,
			CommonURLParam.CLIENT,
			CommonURLParam.INVESTMENT_MANAGER,
			CommonURLParam.ADVISER,
		]);
	}

	/**
	 * Validates that url parameter exists for all the fields defined in formParameters
	 * and sets/clears error messages.
	 */
	protected validateUrlParameters() {
		const missingUrlFormParamList = [];
		for (const formParameter of this.getFormParameters()) {
			const matchingUrlFormParam = this.selectedParameters.find(
				(selectedFormParam) => selectedFormParam.name === formParameter,
			);
			if (!matchingUrlFormParam) {
				missingUrlFormParamList.push(formParameter);
			}
		}
		this.setMissingFormParameterError(missingUrlFormParamList);
	}

	private setMissingFormParameterError(missingFormParamList: string[]): void {
		if (missingFormParamList.length === 0) {
			this.errorMessage = null;
			return;
		}
		// map all form parameters (e.g, CTz_Fund, RGz_Payroll_Records) to FormParameterType (e.g, fund, employer)
		const missingParamTypeList = missingFormParamList.map(
			(missingFormParam) => {
				const missingFieldNameParamType =
					this.fieldNamesToFormParamType.find((fieldNameParamType) =>
						missingFormParam.endsWith(
							fieldNameParamType.fieldNameEndsWith,
						),
					);
				if (!missingFieldNameParamType) {
					Logger.error('Error in form configuration');
					throw new Error(
						'Unknown form parameter: ' + missingFormParam,
					);
				}
				return String(missingFieldNameParamType.paramType);
			},
		);
		// Find the first missing url parameter from missingURLParamErrorMap, use error message if found.
		const missingURLParamError = this.missingURLParamErrorMap.find(
			(p) => missingParamTypeList.indexOf(p[0]) > -1,
		);
		if (missingURLParamError) {
			this.errorMessage = missingURLParamError[1];
			return;
		}
		// Fall to a generic missing parameter error message and take the first missing FormParameterType
		this.errorMessage = this.i18n.tr('errors.missing-a-param-in-url', {
			'missing-param': missingParamTypeList[0],
		});
	}

	private onClickRecord = (record: { [fieldName: string]: any }): void => {
		const newRecordId: number =
			FormModelUtils.getIdFromIdentityField(record);
		const readOnlyRecords: number[] =
			this.recordSelectorTable.readOnlyRecords;
		if (this.slideOut && this.slideOut.isInEditMode) {
			this.promptToClosePanelThen(() => {
				this.updateRecord(record, newRecordId, readOnlyRecords);
				this.recordSelectorTable.clearTableSelection();
				this.addSelectedClassToTableRow();
			});
		} else {
			this.updateRecord(record, newRecordId, readOnlyRecords);
		}
	};

	protected updateRecord(
		record: { [fieldName: string]: any },
		newRecordId: number,
		readOnlyRecords: number[],
	): void {
		if (this.recordId === newRecordId && this.slideOut?.isOpen()) {
			this.slideOut.close();
		} else {
			if (this.recordId !== newRecordId) {
				this.isRowLoading = true;
			}
			this.recordId = newRecordId;
			this.setSelectedRecordIsReadOnly(readOnlyRecords);
			this.updateSelectedRecord();
		}
	}

	private async initRecordSelectorTable() {
		this.recordSelectorTable.setListeners(this.onClickRecord);

		if (this.recordSelectorTable.isAjaxTable) {
			this.setAjaxDataToDefault();
			if (!this.deferAjaxTableDataLoad) {
				this.loadAjaxTableData();
				await this.recordSelectorTable.onAjaxLoaded();
			} else {
				await this.recordSelectorTable.onTableInitialised();
			}
		} else {
			await this.recordSelectorTable.onTableInitialised();
		}

		// Reverse order shortcut is not coupled with bindAcurityTableShortcuts function
		// because it is tied to a paginate element in the DOM which need to be rendered first
		const reverseButtonEl = this.element.querySelectorAll(
			'.dataTables_reverse-order a',
		);
		if (reverseButtonEl && reverseButtonEl.length > 0) {
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.ReverseOrder,
				reverseButtonEl,
			);
		}

		this.initSlideOutSize();
	}

	/**
	 * Initialize the slide out size handling.
	 * To support the dynamic table configure loading logic, the timeout is required
	 * to wait for table columns and rows generated.
	 *
	 * Note: this.slideOut?.setRecordSlideoutSize may not be available all the time,
	 * e.g.: in audit details panel
	 */
	private initSlideOutSize(): void {
		if (this.slideOut?.setRecordSlideoutSize) {
			this.changeSlideOutSize();
			window.addEventListener('resize', this.changeSlideOutSize);
		}
	}

	/**
	 * Change slideout size based on window size
	 */
	private changeSlideOutSize() {
		this.slideOut.setRecordSlideoutSize(
			this.recordSelectorTable.UNSAFE_DEPRECATED_getTableReference(),
			this.visibleColumns,
		);
	}

	/**
	 * Perform any form specific formmodel changes here.
	 * This is invoked for both add form model and edit form model.
	 * @param {FormModel} formModel
	 * @param {FormModel} oldValue
	 */
	protected formModelChanged(
		formModel: FormModel<FieldNameType>,
		oldValue?: FormModel<FieldNameType>,
	) {
		if (!formModel || !formModel.fields) {
			return;
		}
		// no-op
	}

	protected setAjaxDataToDefault() {
		this.recordSelectorTable.setAjaxData((data) => {
			const filteredParameterTypes: FormParameterType[] =
				this.getFilteredParameterTypes();
			this.selectedParameters.forEach((param) => {
				if (
					filteredParameterTypes.some(
						(filterParamType) =>
							param.paramType === filterParamType,
					)
				) {
					if (ObjectUtils.notEmpty(param.value)) {
						const newFilter: DataTableRequestFilter = {
							data: param.name,
							value: param.value,
							operation: FilterType.EQUALS,
							not: false,
							ignoreCase: false,
						};

						const filterIndex = data.filters.findIndex((e) => {
							return e.data === newFilter.data;
						});
						if (filterIndex < 0) {
							data.filters.push(newFilter);
						} else {
							data.filters[filterIndex] = newFilter;
						}
					}
				}
			});
			data.filters.push(...this.paramFilters);
		});
	}

	protected getFilteredParameterTypes(): FormParameterType[] {
		return [
			CommonURLParam.FUND,
			CommonURLParam.MEMBER,
			CommonURLParam.EMPLOYER,
			CommonURLParam.CLIENT,
			CommonURLParam.INVESTMENT_MANAGER,
			CommonURLParam.ADVISER,
			CommonURLParam.TRANS_REF,
			CommonURLParam.DIVISION,
			CommonURLParam.BATCH_REF,
			CommonURLParam.RECEIPT_REF,
			CommonURLParam.RETENTION_PROCESSING_ID,
		];
	}

	/**
	 * slideoutContextMenu is to be used by overriding classes only.
	 */
	@computedFrom('formModel') // NOTE: We need this to ensure Slideout doesn't keep generating this "slideoutContextMenu" in a loop.
	public get slideoutContextMenu(): ContextMenuItem[] {
		// WARNING:
		// Do not add items here as this property is overriden
		// by subclasses.
		// Check slideout.ts for defaults.
		return [];
	}

	protected loadAjaxTableData() {
		let isEmptyTable: boolean = false;
		const useArchive: string = this.queryParams.get(
			CommonURLParam.USE_ARCHIVE,
		);
		if (useArchive) {
			if (this.archiveInProgress || this.archiveDbUpgradeInProgress) {
				let process: string = 'Archive';
				if (this.archiveDbUpgradeInProgress) {
					process += ' Database Upgrade';
				}
				Notification.error(
					this.i18n.tr('archive.data-not-available', { process }),
				);
				isEmptyTable = true;
			}
		}

		this.recordSelectorTable.initAjaxTable(
			this.classicFormsApi.getRecordSelectorTableDataApiUrl(
				this.functionName,
				this.queryParams,
				isEmptyTable,
			),
		);
	}

	protected refreshAjaxTableData() {
		if (this.recordSelectorTable) {
			const url = this.classicFormsApi.getRecordSelectorTableDataApiUrl(
				this.functionName,
				this.queryParams,
			);
			this.recordSelectorTable.setAjaxURL(url);
			this.recordSelectorTable.refreshAjaxTable(true);
		} else {
			Logger.warn(
				'Unable to refresh ajax table data since there is no table',
			);
		}
	}

	/**
	 * This needs to execute after updateSelectedRow as it expects recordId to be set when it opens the slideOut record
	 */
	private async updateSelectedRecord(): Promise<void> {
		if (this.filterIsOpen()) {
			this.recordTableFilter.close();
			this.addSelectedClassToTableRow();
		}
		if (this.slideOut) {
			await this.slideOut.showSlideout();
			this.slideOut.enableReadonlyMode();
		}
	}

	/**
	 * This is for performing dirty checks when changing table rows. If there are no changes, 'doSomething' will
	 * proceed immediately. If there are changes, the original row will be re-selected and a confirmation
	 * dialog is displayed. 'doSomething' will proceed if the dialog is confirmed.
	 *
	 * @param doSomething Run this function once the dirty check is complete
	 */
	private promptToClosePanelThen(doSomething: () => void) {
		const hasChanges = this.slideOut.checkPanelForChanges();
		if (!hasChanges) {
			doSomething();
			return;
		}

		const event = window.event; // get a reference to the event that triggered this
		if (event) {
			event.preventDefault();
		}

		// re-select the original table record
		this.addSelectedClassToTableRow();
		this.slideOut.dirtyCheckWithDialog(hasChanges).then(() => {
			if (event) {
				event.target.dispatchEvent(event); // re-fire the table row click
			}
			doSomething();
		});
	}

	private initContextRow() {
		if (this.slideOut && this.slideOut.isOpen()) {
			this.promptToClosePanelThen(() => this.slideOut.close());
		}
	}

	private contextView() {
		if (!this.updateRowAndCanDoContextMenuAction()) {
			return;
		}
		this.updateSelectedRecord();
	}

	/**
	 * contextAdd will be called if you press the shortcut key to add a new record.
	 */
	private contextAdd() {
		this.openAddNewRecord();
	}

	/**
	 * openAddNewRecord will open if you are not currently editing
	 * a record, if no modal is open and if you have permission to.
	 */
	protected openAddNewRecord() {
		if (!this.canAddRecord || this.formIsEditingRecord()) {
			return;
		}
		this.showClassicPanelDialog(FormType.ADD, this.addCustomisableAreaName);
	}

	// NOTE: Jake: 2019-09-12 - WEBUI-2348
	// Disabled as cloning behaviour is put on-hold, as per this JIRA.
	// We may want to uncomment this and bring it back in the future
	/* private readonly contextCloneCurrentlySelectedRecord = (): void => {
		if (!this.updateRowAndCanDoContextMenuAction()) {
			return;
		}
		this.slideOut.close();
		this.cloneCurrentlySelectedRecord();
		this.recordSelectorTable.clearTableSelection();
	}*/

	/**
	 * cloneCurrentlySelectedRecord will open a modal thats pre-loaded with the record
	 * data you want to clone.
	 */
	protected cloneCurrentlySelectedRecord() {
		this.showClassicPanelDialog(
			FormType.ADD,
			this.addCustomisableAreaName,
			this.recordId,
		);
	}

	private async contextEdit() {
		if (!this.updateRowAndCanDoContextMenuAction()) {
			return;
		}
		if (this.selectedRecordIsReadOnly) {
			return;
		}
		await this.slideOut.showSlideout();
		this.slideOut.enableReadonlyMode();
		this.slideOut.edit();
	}

	protected contextDelete() {
		if (!this.updateRowAndCanDoContextMenuAction()) {
			return;
		}
		if (this.selectedRecordIsReadOnly) {
			return;
		}
		this.getFormModelAndConfirm();
	}

	protected getFormModelAndConfirm() {
		this.classicFormsApi
			.getClassicFormData(this.functionName, this.recordId)
			.then((model) => {
				this.showDeleteRecordDialog(model);
			});
	}

	get selectedRecordIsReadOnly() {
		return this._selectedRecordIsReadOnly;
	}

	private setSelectedRecordIsReadOnly(readOnlyRecords: number[]) {
		this._selectedRecordIsReadOnly =
			readOnlyRecords && readOnlyRecords.indexOf(this.recordId) !== -1;
	}

	protected setRecordToReadOnly(readOnly: boolean): void {
		this._selectedRecordIsReadOnly = readOnly;
	}

	/**
	 * Show dialog to delete a record
	 */
	public showDeleteRecordDialog(model?: FormModel): void {
		if (this.selectedRecordIsReadOnly) {
			if (DEVELOPMENT) {
				throw new Error(
					'Should not call showDeleteRecordDialog() if selectedRecordIsReadOnly is true.',
				);
			}
			return;
		}
		model = model === undefined ? this.formModel : model;
		const softDeleteFieldModel = FormModelUtils.getDeletedField(model);

		let body: string;
		let deleteYes: boolean;
		if (softDeleteFieldModel) {
			deleteYes = softDeleteFieldModel.value !== 'Y';
			body = this.i18n.tr(DeleteHelper.confirmMessage(deleteYes));
		} else {
			deleteYes = true;
			body = this.i18n.tr('delete-record-modal.confirm-hard-delete');
		}
		this.dialogUtils.showDialog({
			simpleDialogType: SimpleDialogType.WARNING,
			bodyText: body,
			okFunction: () => this.deleteRecord(deleteYes),
		});
	}

	/**
	 * Sends ajax DELETE request to server to soft delete (Deleted = Y), un-delete (Deleted = N)
	 * or hard delete the record from the database table.
	 * Whether it is a hard or soft delete is controlled by the ajax API implementation.
	 * Hard delete closes slideout and refreshes the table as the record no longer exists while
	 * soft delete updates the delete button label and slideout with the returned form model.
	 * This API call no longer toggles soft delete to ensure DELETE operations are idempotent.
	 * @param {boolean} deleteYes	The final state that we want to set the Deleted field to (for a soft delete).
	 * True if setting Deleted flag to Y (soft delete), False if setting to N (un-delete).
	 */
	protected deleteRecord(deleteYes: boolean) {
		const resultMessages = DeleteHelper.resultMessages(deleteYes);

		// API controls which records can be hard deleted
		this.classicFormsApi
			.deleteRecord(
				this.recordId,
				this.functionName,
				deleteYes,
				this.deleteQueryParams,
			)
			.then((model) => {
				if (FormModelUtils.getDeletedField(model)) {
					// The backend did a soft delete so the record still exists.
					this.slideOut.setFormModelIfOpen(model);
				} else {
					this.slideOut.close();
				}
				FormModelUtils.showWarningMessagesIfPresent(model);
				Notification.success(this.i18n.tr(resultMessages.success));
				this.publishEvent(Slideout.Events.PostSave, {
					source: this.functionName,
				});
				this.enableClickableElements();
			})
			.catch((reason) => {
				Notification.error(
					this.i18n.tr(resultMessages.error) + reason.message,
				);
				this.enableClickableElements();
			});
	}

	/**
	 * This updates the value of recordId and sets the selected row in the recordSelectorTable
	 */
	protected updateSelectedRow() {
		const prevRecordId = this.recordId;
		if (this.recordSelectorTable.currentRowGetter() != null) {
			this.recordId = parseInt(
				this.recordSelectorTable
					.currentRowGetter()
					.attr('id')
					.toString(),
				10,
			);
		} else {
			this.recordId = Number(this.recordSelectorTable.selectedObjId);
		}
		this.setSelectedRecordIsReadOnly(
			this.recordSelectorTable.readOnlyRecords,
		);
		this.addSelectedClassToTableRow();

		if (this.recordId !== prevRecordId) {
			this.isRowLoading = true;
		}
	}

	private addSelectedClassToTableRow() {
		this.recordSelectorTable.setSelectedRecordId(this.recordId);
	}

	protected async contextExport() {
		if (!this.updateRowAndCanDoContextMenuAction()) {
			return;
		}
		this.disableClickableElements();
		if (this.recordSelectorTable.isAjaxTable) {
			this.ajaxTableExport();
		} else {
			// use functionname as export filename
			this.recordSelectorTable.normalTableExport(this.functionName);
		}
		this.enableClickableElements();
	}

	private async ajaxTableExport(): Promise<void> {
		const exportInstruction = { doExport: true, numberToExport: 0 };
		const closeResult = await this.dialogUtils
			.showCustomDialog(
				ExcelExportDialog,
				this.recordSelectorTable.getExportDialogModel(),
			)
			.whenClosed((value) => value);
		exportInstruction.doExport = !closeResult.wasCancelled;
		if (exportInstruction.doExport) {
			exportInstruction.numberToExport = (
				closeResult.output as ExcelExportDialogModel
			).numberToExport;
			await this.exportAjaxTableRows(
				exportInstruction.numberToExport,
			).catch((error) => {
				Notification.error(
					this.i18n.tr('errors.failed-to-export-data-to-csv') +
						'. ' +
						error.error,
				);
			});
		}
	}

	private static hasDeletedColumn(columnsToExport: ExportColumn): boolean {
		return (
			Object.keys(columnsToExport).find((columnName) =>
				FormModelUtils.isDeletedField(columnName),
			) !== undefined
		);
	}

	private async exportAjaxTableRows(numberToExport: number): Promise<void> {
		const exportQueryParams: Map<string, string> = this.queryParams;
		const columnsToExport: ExportColumn = this.getColumnsToExport();

		// add deleted field to CSV columns
		if (!ClassicForm.hasDeletedColumn(columnsToExport)) {
			let deletedField;
			// try to get deleted field from table
			if (this.recordSelectorTable.deletedHighlightField) {
				deletedField = this.recordSelectorTable.deletedHighlightField;
			} else {
				Logger.warn(
					'ClassicForm:export',
					'deletedHighlightField is not specified for ' +
						this.functionName,
				);
				// try to get deleted field from form model
				deletedField = FormModelUtils.getDeletedField(this.formModel)
					? FormModelUtils.getDeletedField(this.formModel).name
					: null;
			}
			// only add deleted field to column headers if field name found
			if (deletedField) {
				columnsToExport[deletedField] = 'Deleted';
			} else {
				Logger.warn(
					'ClassicForm:export',
					'Could not find deleted field. Please check that ' +
						this.functionName +
						' can be deleted.',
				);
			}
		}

		exportQueryParams.set(
			'column-headers',
			JSON.stringify(columnsToExport),
		);

		const dataGetter = (tableParams: APIParameters) =>
			this.classicFormsApi.getCSVData(
				this.functionName,
				this.recordSelectorTable.validateNumberToExport(numberToExport),
				tableParams,
				exportQueryParams,
			);

		await this.recordSelectorTable.ajaxTableExport(
			this.functionName,
			dataGetter,
		);
	}

	protected getColumnsToExport(): ExportColumn {
		return this.recordSelectorTable.getColumnsToExport();
	}

	protected contextNotes() {
		if (!this.updateRowAndCanDoContextMenuAction()) {
			return;
		}
		this.showNotesDialog();
	}

	protected showNotesDialog() {
		return this.slideOut.showNotesDialog();
	}

	protected updateRowAndCanDoContextMenuAction(): boolean {
		this.updateSelectedRow();
		if (this.formIsEditingRecord() || !this.recordId) {
			return false;
		}
		// need to manually set record id as binding doesn't fire if slideout not open
		this.slideOut.recordIdentity = this.recordId;
		return true;
	}

	private contextAuditDetails(): void {
		if (!this.updateRowAndCanDoContextMenuAction()) {
			return;
		}
		this.slideOut.showAuditDialog();
	}

	protected disableClickableElements() {
		this.recordSelectorTable.toggleSpinner(true);
		$('[data-toggle="tooltip"]').tooltip('hide');
		this.setClickable(false);
		if (this.recordTableFilter != null) {
			this.recordTableFilter.canClick = false;
		}
	}

	protected enableClickableElements() {
		this.recordSelectorTable.toggleSpinner(false);
		this.setClickable(true);
		if (this.recordTableFilter != null) {
			this.recordTableFilter.canClick = true;
		}
	}

	protected setClickable(clickable: boolean) {
		this.canClick = clickable;
		this.slideOut.setCanClick(clickable);
	}

	protected openSelected() {
		if (document.body.classList.contains('ux-dialog-open')) {
			return;
		}
		if (
			this.recordSelectorTable.currentRowGetter() &&
			this.recordSelectorTable.currentRowGetter().is(':focus')
		) {
			this.updateSelectedRecord();
		}
	}

	private canUseSelectRecordOrPage = (): boolean => {
		return !this.formIsEditingRecord() && !this.filterIsOpen();
	};

	private selectNextRecord = (): void => {
		this.selectSpecificRecord(RecordIdentifier.NEXT);
	};

	private selectPreviousRecord = (): void => {
		this.selectSpecificRecord(RecordIdentifier.PREVIOUS);
	};

	private selectSpecificRecord = (
		recordIdentifier: RecordIdentifier,
	): void => {
		if (this.canUseSelectRecordOrPage()) {
			this.recordSelectorTable
				.selectSpecificRecord(recordIdentifier)
				.then((selected) => this.updateSelectedRecordForRow(selected));
		}
	};

	private selectFirstPage = (): void => {
		this.selectPage(PageIdentifier.FIRST);
	};

	/**
	 * As per WEBUI-2427, Classic Form tables no longer support jumping to last page. Replaced by Reverse Order
	 * @deprecated
	 */
	private selectLastPage = (): void => {
		this.selectPage(PageIdentifier.LAST);
	};

	private selectNextPage = (): void => {
		this.selectPage(PageIdentifier.NEXT);
	};

	private selectPreviousPage = (): void => {
		this.selectPage(PageIdentifier.PREVIOUS);
	};

	private selectPage = (pageIdentifier: PageIdentifier): void => {
		if (this.canUseSelectRecordOrPage()) {
			this.recordSelectorTable
				.selectSpecificPage(pageIdentifier)
				.then((selected) => this.updateSelectedRecordForRow(selected));
		}
	};

	/**
	 * Update the selected recordId to match the specified table row
	 */
	private updateSelectedRecordForRow = ($newSelectedRow: JQuery): void => {
		if (!$newSelectedRow || $newSelectedRow.length === 0) {
			return;
		}
		this.recordId = Number($newSelectedRow.attr('id'));
		this.setSelectedRecordIsReadOnly(
			this.recordSelectorTable.readOnlyRecords,
		);
		if (this.slideOut.isOpen()) {
			$newSelectedRow.click();
		} else {
			$newSelectedRow.focus();
		}
	};

	private clearTableSelection() {
		if ($('body').hasClass('ux-dialog-open')) {
			return;
		}
		// cancel edit and close slideout handled by keyboard shortcuts in slideout.ts
		if (!this.slideOut.isOpen()) {
			$('#' + this.recordSelectorTable.tableId + ' tr.selected').blur();
			this.recordSelectorTable.clearTableSelection();
			this.recordId = null;
			this._selectedRecordIsReadOnly = false;
		}
	}

	protected closeRecord() {
		if (this.slideOut) {
			this.slideOut.close();
		}
		this.returnFocusToSelectedRow();
	}

	protected openFilterSlideout() {
		this.recordTableFilter.open();
	}

	private returnFocusToSelectedRow(): void {
		if (!this.filterIsOpen()) {
			this.setFocusToSelectedRow();
		}
	}

	/**
	 * @returns {boolean} true if the row of the selected record id is currently in the table.
	 */
	private setFocusToSelectedRow(): boolean {
		if (this.recordSelectorTable) {
			const $tableRowEl = this.recordSelectorTable.currentRowGetter();
			const isRowFocusable = $tableRowEl && $tableRowEl.length > 0;
			if (isRowFocusable) {
				$tableRowEl.focus();
			}
			return isRowFocusable;
		}
		return false;
	}

	/**
	 * formIsEditingRecord will return true if a modal is open or if you're
	 * editing a record in a slideout.
	 */
	protected formIsEditingRecord(): boolean {
		return (
			(this.slideOut && this.slideOut.isInEditMode) ||
			document.body.classList.contains('ux-dialog-open')
		);
	}

	private filterIsOpen(): boolean {
		// prevent event from firing if filter exists and is open
		return this.recordTableFilter && this.recordTableFilter.isFilterOpen;
	}

	private getPreSelectedParams(
		preSelectableParameterTypes: FormParameterType[],
	): FormParameters[] {
		const preSelectedParams: FormParameters[] = [];
		// only want to preselect certain parameters, category should not be pre-populated or set to read only in classic panel dialog
		this.selectedParameters.forEach((selectedParameter) => {
			if (
				preSelectableParameterTypes.some(
					(paramType) => selectedParameter.paramType === paramType,
				)
			) {
				preSelectedParams.push(selectedParameter);
			}
		});
		return preSelectedParams;
	}

	protected getPreSelectedParamsForAdd(): FormParameters[] {
		return this.getPreSelectedParams([
			CommonURLParam.FUND,
			CommonURLParam.MEMBER,
			CommonURLParam.EMPLOYER,
			CommonURLParam.CLIENT,
			CommonURLParam.INVESTMENT_MANAGER,
			CommonURLParam.ADVISER,
			CommonURLParam.DIVISION,
			CommonURLParam.BATCHES,
		]);
	}

	protected getClassicPanelDialogModel(
		dialogFormType: FormType,
		customisableAreaName: string,
		recordIdentity?: number,
	): ClassicPanelDialogModel {
		const classicPanelModel: ClassicPanelDialogModel = {
			functionName: this.functionName,
			formType: dialogFormType,
			customisationConfig: this.customisationConfig,
			areaName: customisableAreaName,
			recordId: recordIdentity,
			preSelectedParameters: this.getPreSelectedParamsForAdd(),
			extraReadOnlyParameters: this.extraReadOnlyParameters,
			queryParams: this.queryParams,
			onFormLoad: (formModel) => this.formModelChanged(formModel),
			nonCustomisableAreaHtml: this.nonCustomisableAreaHtml,
		};
		return classicPanelModel;
	}

	public showClassicPanelDialog(
		dialogFormType: FormType,
		customisableAreaName: string,
		recordId?: number,
	) {
		if (this.slideOut.isInEditMode) {
			// should not allow two classic panels to be edited
			this.slideOut.cancelEditOrClose();
		} else {
			this.classicPanelDialogUtils.showClassicPanelDialog(
				this.getClassicPanelDialogModel(
					dialogFormType,
					customisableAreaName,
					recordId,
				),
				this.recordSelectorTable,
			);
		}
	}

	/**
	 * If valid table column configs are declared in the JSON customisation, the new columns overwrite the old columns so that the
	 * customisation can be used to add or remove columns. If any of the new and old column names match, the label of the new column
	 * is merged with the existing properties of old column. If a `fieldConfig` that matches the corresponding `tableColumnConfig`
	 * has a `dataType` declared, i.e. `dataTypeMap` is not null, use this `dataType` if `tableColumnConfig.dataType` is null.
	 * If a table column config is for a calculated field, i.e. `name` starts with `ZZ` or `ZY`, set the column to be
	 * unsortable as the backend does not support sorting calculated fields.
	 */
	private mergeTableColumns(
		defaultColumns: TableColumnDefinition[],
		customTableColumnsConfig: TableColumnDefinition[],
		dataTypeMap: Map<string, DataType>,
	): TableColumnDefinition[] {
		const resolveDependedOnField = (fieldName) => {
			if (
				customTableColumnsConfig.find(
					(config) => config.name === fieldName,
				)
			) {
				return;
			}
			const columnDefinition = defaultColumns.find(
				(column) => column.name === fieldName,
			);
			if (!columnDefinition) {
				Notification.error(
					this.i18n.tr('errors.error-loading-customisation') +
						' ' +
						this.i18n.tr('errors.table-column-invalid-depends-on', {
							fieldName,
						}),
				);
				return;
			}
			columnDefinition.hiddenFrom = [];
			this.pushWithDuplicationCheck(mergedColumns, columnDefinition);
			if (columnDefinition.dependsOn?.length) {
				columnDefinition.dependsOn.forEach(resolveDependedOnField);
			}
		};

		const mergedColumns: TableColumnDefinition[] = [];
		for (const customColumnConfig of customTableColumnsConfig) {
			if (!customColumnConfig.name || !customColumnConfig.label) {
				// customisation must have name and label for every column
				// if dataType missing, default to String
				Notification.error(
					this.i18n.tr('errors.error-loading-customisation') +
						' ' +
						this.i18n.tr(
							'errors.table-column-config-properties-missing',
						),
				);
				return defaultColumns;
			}

			if (
				customColumnConfig.name.startsWith(
					FormModelUtils.CUSTOM_FIELD_PREFIX,
				) ||
				customColumnConfig.name.startsWith(
					FormModelUtils.CUSTOMISATION_LEVEL_USER_DEFINED_PREFIX,
				)
			) {
				customColumnConfig.unsortable = true;
			}

			// Order of priority: `tableColumnConfig.dataType` > `fieldConfig.dataType` > String `dataType`
			const dataType: DataType = customColumnConfig.dataType ||
				dataTypeMap.get(customColumnConfig.name) || {
					name: DataTypeEnum.String,
				};
			// copy default column to new column to preserve existing default properties
			const mergedColumn = defaultColumns.find(
				(defaultColumn) =>
					customColumnConfig.name === defaultColumn.name,
			);
			if (mergedColumn) {
				// overwrite default label and dataType with custom value
				mergedColumn.label = customColumnConfig.label;
				mergedColumn.dataType = dataType;
				mergedColumn.hiddenFrom = undefined;
				this.pushWithDuplicationCheck(mergedColumns, mergedColumn);

				if (mergedColumn.dependsOn?.length) {
					mergedColumn.dependsOn.forEach(resolveDependedOnField);
				}
			} else {
				customColumnConfig.dataType = dataType;
				this.pushWithDuplicationCheck(
					mergedColumns,
					customColumnConfig,
				);
			}
		}
		this.recordSelectorTable.customColumns = true;
		return mergedColumns;
	}

	private pushWithDuplicationCheck(
		columns: TableColumnDefinition[],
		columnToPush: TableColumnDefinition,
	) {
		if (!columns.some((column) => column.name === columnToPush.name)) {
			columns.push(columnToPush);
		}
	}

	/**
	 * Get field value from current formModel
	 */
	protected getFormModelFieldValue(
		fieldName: string,
		formModel?: FormModel,
	): string | undefined {
		if (!formModel) {
			formModel = this.formModel;
		}
		const fieldModel = FormModelUtils.getFieldModel(formModel, fieldName);
		return fieldModel ? fieldModel.value : undefined;
	}

	private showNoCustomisationDialog() {
		const dialogModel = this.dialogUtils.getSimpleDialogModel({
			buttons: [SimpleDialogButtons.OK],
			bodyText: this.i18n.tr('infos.no-customisation'),
		});
		this.dialogUtils.showCustomDialog(SimpleDialog, dialogModel);
	}
}
